import { FC, JSX, useEffect, useState } from "react";
import { Container, Heading, Text, Flex, Box, Tabs } from "@radix-ui/themes";
import { useUpdateProfile } from "@/features/profile/hooks/useUpdateProfile";
import { timestamp } from "@/api/firebase/firebase.config";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import ProfileForm from "@/features/profile/components/ProfileForm/ProfileForm";
import { Profile } from "@/models/Profile.model";
import ProfileAboutForm from "../ProfileForm/ProfileAboutForm";
import ImageUpload from "@/components/ImageUpload/ImageUpload";

const ProfileUpdate: FC = (): JSX.Element => {
  const profile = useCurrentUserProfile((s) => s.user);
  const { updateProfile, loading, error } = useUpdateProfile();
  const [avatarURL, setAvatarURL] = useState<string | null>(null);
  const [coverURL, setCoverURL] = useState<string | null>(null);

  const initialValues: Profile = {
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    uid: profile.uid || "",
    email: profile.email || "",
    createdAt: profile.createdAt || timestamp.now(),
    onboarded: profile.onboarded || false,
    nationality: profile.nationality || "",
    countryOfResidence: profile.countryOfResidence || "",
    gender: profile.gender || "",
    phoneNumber: profile.phoneNumber || "",
    dateOfBirth: profile.dateOfBirth || null,
    about: profile.about || "",
    avatarURL: profile.avatarURL || "",
    coverURL: profile.coverURL || "",
  };

  useEffect(() => {
    if (avatarURL) {
      updateProfile({ ...initialValues, avatarURL });
    }
  }, [avatarURL]);

  useEffect(() => {
    if (coverURL) {
      updateProfile({ ...initialValues, coverURL });
    }
  }, [coverURL]);

  return (
    <>
      <Container px={{ initial: "4", lg: "0" }}>
        <Flex direction="column" gap="2" mb="5">
          <Heading size="8" as="h1" mb="0">
            Settings
          </Heading>
          <Text color="gray">
            Manage your account settings and tell us more about yourself.
          </Text>
        </Flex>

        <Tabs.Root defaultValue="account">
          <Tabs.List>
            <Tabs.Trigger value="account">Account</Tabs.Trigger>
            <Tabs.Trigger value="about">About</Tabs.Trigger>
            <Tabs.Trigger value="avatar">Profile image</Tabs.Trigger>
            <Tabs.Trigger value="cover">Cover photo</Tabs.Trigger>
          </Tabs.List>

          <Box pt="3">
            <Tabs.Content value="account">
              <ProfileForm
                initialValues={initialValues}
                onSubmit={updateProfile}
                loading={loading}
                error={error}
                submitButtonText="Update Profile"
              />
            </Tabs.Content>

            <Tabs.Content value="about">
              <ProfileAboutForm
                initialValues={initialValues}
                onSubmit={updateProfile}
                loading={loading}
                error={error}
                submitButtonText="Update About"
              />
            </Tabs.Content>
            <Tabs.Content value="avatar">
              <Heading>Upload Profile Image</Heading>
              <ImageUpload
                location={`users/${profile.uid}/avatar`}
                setImageUrl={setAvatarURL}
              />
            </Tabs.Content>
            <Tabs.Content value="cover">
              <Heading mb="1">Upload a cover photo</Heading>
              <Text as="p" size="2" color="gray" mb="4">
                The aspect ratio on desktop is 5:1. On smaller screens it is
                5:2. On mobile devices the aspect ratio is 2:1
              </Text>
              <ImageUpload
                location={`users/${profile.uid}/cover`}
                setImageUrl={setCoverURL}
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Container>
    </>
  );
};

export default ProfileUpdate;
